import { Observable } from 'rxjs';
import { PlaybackController, PlaybackState } from '../../contracts/playback-controller';
import { ToolbarControl, ToolbarControlContext } from '../../contracts/toolbar-control.tokens';

/**
 * Base wiring shared by every toolbar control (context accessor + state stream).
 *
 * Deliberately a PLAIN abstract class: decorating it with `@Directive()` trips
 * NG2004 under AOT, because the concrete controls supply `context` through
 * their own constructors rather than through inherited DI metadata. That also
 * means this class must never gain decorated members (@Input, @HostListener…) —
 * Angular does not inherit those from an undecorated base.
 */
export abstract class BaseControl implements ToolbarControl {
  abstract context: ToolbarControlContext;

  get ctrl(): PlaybackController {
    return this.context.controller;
  }

  get state$(): Observable<PlaybackState> {
    return this.context.controller.state$;
  }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad2 = (n: number) => String(n).padStart(2, '0');

/** "21 Jul 2026, 20:02:28 UTC" — the timeline's full stamp. */
export function formatUtcStamp(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return (
    `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}, ` +
    `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())} UTC`
  );
}

/**
 * "21 Jul 20:02 UTC" — the scrub bubble.
 *
 * Between the other two on purpose: the bubble floats over the chart while you
 * drag, so it has to fit, but a bare clock is ambiguous on a track that spans 90
 * days. Seconds are what gets dropped — a decimated transport never lands on one
 * that means anything. Matches `trackTime` on the sidebar's ON MAP track bar, so
 * the two clocks read identically.
 */
export function formatUtcShort(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return (
    `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ` +
    `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())} UTC`
  );
}
